import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import EditPaymentModal from '../components/payments/EditPaymentModal';
import AddContributionModal from '../components/payments/AddContributionModal';
import { useAuth } from '../context/AuthContext';
import { Search, AlertTriangle, TrendingUp, Mail, Plus, X, Check, CreditCard } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v ?? 0);

const PaymentsPage = ({ formatCurrency: fmtProp, onPaymentUpdated }) => {
  const formatCurrency = fmtProp || fmt;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperUser = user?.role === 'super_user';

  const [paymentSummary, setPaymentSummary] = useState(null);
  const [overduePayments, setOverduePayments] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [unpaidPayments, setUnpaidPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingPayment, setEditingPayment] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const fetchData = async () => {
    try {
      const [overdue, recent, summary, unpaid] = await Promise.all([
        apiService.getOverduePayments(),
        apiService.getPayments(),
        apiService.getPaymentSummary(),
        apiService.getUnpaidPayments()
      ]);
      setOverduePayments(overdue);
      setRecentPayments(recent);
      setPaymentSummary(summary);
      setUnpaidPayments(unpaid);
    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPayment = async (paymentId, formData) => {
    await apiService.updatePayment(paymentId, formData);
    setEditingPayment(null);
    await fetchData();
    if (onPaymentUpdated) await onPaymentUpdated();
  };

  const handleSendReminders = async () => {
    if (!window.confirm('Send overdue payment reminders to all members with email addresses?')) return;
    setSendingReminders(true);
    setReminderResult(null);
    try {
      const result = await apiService.sendOverdueReminders();
      setReminderResult(result);
    } catch (err) {
      setReminderResult({ error: err.message || 'Failed to send reminders' });
    } finally {
      setSendingReminders(false);
    }
  };

  const filteredOverdue = overduePayments.filter(p =>
    p.member_name.toLowerCase().includes(searchTerm.toLowerCase()) || p.membership_ref?.toString().includes(searchTerm));
  const allPayments = filterStatus === 'overdue' ? unpaidPayments
    : filterStatus === 'all' ? [...recentPayments, ...unpaidPayments].sort((a, b) => new Date(b.payment_month) - new Date(a.payment_month))
    : recentPayments;
  const filteredRecent = allPayments.filter(p =>
    p.member_name.toLowerCase().includes(searchTerm.toLowerCase()) || p.membership_ref?.toString().includes(searchTerm));

  if (loading) {
    return (
      <div className="p-3 sm:p-6">
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-400 text-sm">Loading payment data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-6">
      {/* Page title */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Contributions</h1>
          <p className="text-slate-600 text-sm">Manage subscriptions, overdue accounts and payment history</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl shadow-sm transition-colors flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add Contribution
        </button>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search by member name or reference…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs">
            Clear
          </button>
        )}
      </div>
      {searchTerm && (
        <p className="text-sm text-blue-600 -mt-4">
          Found: {filteredOverdue.length} overdue · {filteredRecent.length} payments
        </p>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-sm text-slate-500">Paid This Month</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{paymentSummary?.paid_this_month || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <span className="text-sm text-slate-500">Outstanding This Month</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{paymentSummary?.outstanding_this_month || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm text-slate-500">Collected This Year</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{formatCurrency(paymentSummary?.total_collected_this_year || 0)}</p>
        </div>
      </div>

      {/* Overdue */}
      {filteredOverdue.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">Overdue Payments ({filteredOverdue.length})</span>
              </div>
              {isSuperUser && (
                <button onClick={handleSendReminders} disabled={sendingReminders}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex-shrink-0">
                  <Mail className="w-4 h-4" />
                  {sendingReminders ? 'Sending…' : 'Send Reminders'}
                </button>
              )}
            </div>
          </div>
          {reminderResult && (
            <div className={`mx-4 mt-4 p-3 rounded-lg text-sm ${reminderResult.error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
              {reminderResult.error ? `Error: ${reminderResult.error}` : `Sent: ${reminderResult.sent}, Failed: ${reminderResult.failed}, Total overdue: ${reminderResult.totalOverdue}`}
            </div>
          )}
          <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {filteredOverdue.map(payment => (
              <div key={`${payment.member_id}-overdue`}
                onClick={() => navigate(`/members/${payment.member_id}`)}
                className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors cursor-pointer">
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{payment.member_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {payment.overdue_months} month{payment.overdue_months > 1 ? 's' : ''} overdue · {formatCurrency(payment.amount)}/mo
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-red-600">{formatCurrency(payment.amount * payment.overdue_months)}</p>
                  <p className="text-xs text-blue-500 mt-0.5">View profile →</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Payments */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-slate-600 to-slate-700 text-white px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              <span className="font-semibold">Payment History</span>
            </div>
            <div className="flex gap-1">
              {['all','paid','overdue'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium capitalize transition-colors
                    ${filterStatus === s ? 'bg-white text-slate-700' : 'bg-white/20 text-white hover:bg-white/30'}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {filteredRecent.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No payment records found</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Member</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Month</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">Paid Date</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">Method</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  {isSuperUser && <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRecent.slice(0, 50).map((payment, idx) => (
                  <tr key={payment.id || `pay-${payment.member_id}-${payment.payment_month}-${idx}`}
                    className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      onClick={() => navigate(`/members/${payment.member_id}`)}>
                      {payment.member_name}
                    </td>
                    <td className="py-3 px-4 font-semibold text-green-600">{formatCurrency(payment.amount)}</td>
                    <td className="py-3 px-4 text-slate-600">
                      {new Date(payment.payment_month).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                    </td>
                    <td className="py-3 px-4 text-slate-500 hidden md:table-cell">
                      {payment.paid_date ? new Date(payment.paid_date).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="py-3 px-4 text-slate-400 capitalize hidden md:table-cell">
                      {payment.payment_method?.replace('_', ' ') || '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full
                        ${payment.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {payment.status}
                      </span>
                    </td>
                    {isSuperUser && (
                      <td className="py-3 px-4">
                        {payment.id && (
                          <button
                            onClick={e => { e.stopPropagation(); setEditingPayment(payment); }}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors">
                            Edit
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {filteredRecent.length > 50 && (
            <p className="text-center text-slate-400 text-xs py-3">Showing 50 of {filteredRecent.length} payments</p>
          )}
        </div>
      </div>

      {editingPayment && (
        <EditPaymentModal
          payment={editingPayment}
          onClose={() => setEditingPayment(null)}
          onSave={handleEditPayment}
        />
      )}

      {showAddModal && (
        <AddContributionModal
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); fetchData(); }}
        />
      )}
    </div>
  );
};

export default PaymentsPage;
